import React, { useState } from 'react';
import CropEditor from './CropEditor';
import { useConfirm } from '../../context/ConfirmContext';
import {
  addItemImage, deleteItemImage, errorMessage, recropItemImage, reorderItemImages,
} from '../../api/catalogImportApi';
import { btnSmall } from './styles';

const iconBtn = {
  width: 24, height: 24, padding: 0, border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff',
  color: '#374151', fontSize: 13, lineHeight: '22px', cursor: 'pointer',
};

/**
 * The draft's listing images, cut from the catalog pages. The first one becomes the product's
 * main image. Every change goes straight to the backend, which re-cuts the crop from the page.
 */
export default function DraftImages({ item, loadPages, disabled, onItemChanged, onError }) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(null); // { image, pages } — image null means add a new crop
  const [busy, setBusy] = useState(false);
  const images = item.images || [];

  const run = async (fn, fallback) => {
    setBusy(true);
    try {
      const res = await fn();
      onItemChanged(res.data);
    } catch (err) {
      onError(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const move = (index, delta) => {
    const ids = images.map((img) => img.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + delta, 0, moved);
    run(() => reorderItemImages(item.id, ids), 'Could not reorder images.');
  };

  const remove = async (image) => {
    const ok = await confirm({ title: 'Remove this image?', message: 'It won\'t be used for the product. You can crop it again from the page.', confirmLabel: 'Remove', tone: 'danger' });
    if (ok) run(() => deleteItemImage(item.id, image.id), 'Could not remove the image.');
  };

  // Page links are short-lived presigned URLs, so fetch fresh ones each time the editor opens.
  const openEditor = async (image) => {
    setBusy(true);
    try {
      setEditing({ image, pages: await loadPages() });
    } catch (err) {
      onError(errorMessage(err, 'Could not load the catalog pages.'));
    } finally {
      setBusy(false);
    }
  };

  // CropEditor shows its own error on a rejected save, so this rethrows instead of using run().
  const saveCrop = async (box) => {
    try {
      const res = editing.image
        ? await recropItemImage(item.id, editing.image.id, box)
        : await addItemImage(item.id, box);
      onItemChanged(res.data);
      setEditing(null);
    } catch (err) {
      throw new Error(errorMessage(err, 'Could not save the crop.'));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {images.length === 0 && (
          <div style={{ width: 120, height: 120, border: '1.5px dashed #FCA5A5', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#B91C1C', textAlign: 'center', padding: 8, boxSizing: 'border-box' }}>
            No images yet
          </div>
        )}
        {images.map((img, i) => (
          <figure key={img.id} style={{ margin: 0, width: 120 }}>
            <div style={{ position: 'relative' }}>
              <img src={img.url} alt={`${item.name || 'Draft'}, crop ${i + 1}`}
                   style={{ width: 120, height: 120, objectFit: 'contain', background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: 8, display: 'block' }} />
              {i === 0 && (
                <span style={{ position: 'absolute', top: 4, left: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#111827', borderRadius: 4, padding: '1px 6px' }}>Main</span>
              )}
              {img.lowRes && (
                <span title={`Only ${img.widthPx}×${img.heightPx} px — low for a product listing`}
                      style={{ position: 'absolute', bottom: 4, left: 4, fontSize: 10, fontWeight: 700, color: '#92400E', background: '#FEF3C7', borderRadius: 4, padding: '1px 6px' }}>
                  Low-res {img.widthPx}×{img.heightPx}
                </span>
              )}
            </div>
            {!disabled && (
              <div style={{ display: 'flex', gap: 4, marginTop: 6, justifyContent: 'center' }}>
                <button style={iconBtn} disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label="Move image earlier" title="Move earlier">←</button>
                <button style={iconBtn} disabled={busy || i === images.length - 1} onClick={() => move(i, 1)} aria-label="Move image later" title="Move later">→</button>
                <button style={iconBtn} disabled={busy} onClick={() => openEditor(img)} aria-label="Adjust crop" title="Adjust crop">✎</button>
                <button style={{ ...iconBtn, color: '#B91C1C' }} disabled={busy} onClick={() => remove(img)} aria-label="Remove image" title="Remove">×</button>
              </div>
            )}
          </figure>
        ))}
        {!disabled && (
          <button style={{ ...btnSmall, height: 120, width: 120 }} disabled={busy} onClick={() => openEditor(null)}>
            + Crop from page
          </button>
        )}
      </div>

      {editing && (
        <CropEditor
          pages={editing.pages}
          title={editing.image ? 'Adjust crop' : 'Crop a new image'}
          initial={editing.image || { pageNumber: item.pageNumber, x1: 0.25, y1: 0.25, x2: 0.75, y2: 0.75 }}
          onSave={saveCrop}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
